/**
 * O formulário de disponibilidade, do lado de quem preenche. P12.
 *
 * A mesma conta que o servidor faz em `api/src/formulario.ts`, repetida aqui
 * para o professor ver **enquanto marca** se já cobre o que foi pedido — em
 * vez de descobrir só no envio. O servidor confere de novo: esta é a
 * conveniência, aquela é a regra.
 *
 * Fica fora de `.tsx` para o Node conseguir testar sem JSX.
 */

export interface TempoDoFormulario {
  dia: number
  inicio: number
  fim: number
  turno_id: string
}

export interface ExigenciaDoFormulario {
  turno_id: string | null
  dia: number | null
  aulas: number
}

export const chaveDoTempo = (t: { dia: number; inicio: number; fim: number }) => `${t.dia}-${t.inicio}-${t.fim}`

/** Quantos horários liberados contam para cada exigência, na ordem delas. */
export function contagem(
  exigencias: readonly ExigenciaDoFormulario[],
  tempos: readonly TempoDoFormulario[],
  marcados: ReadonlySet<string>,
): number[] {
  const livres = tempos.filter((t) => marcados.has(chaveDoTempo(t)))
  return exigencias.map(
    (e) =>
      livres.filter((t) => (e.turno_id === null || t.turno_id === e.turno_id) && (e.dia === null || t.dia === e.dia))
        .length,
  )
}

/** Cobre tudo? Cada exigência e o total da semana. */
export function cobre(
  pedido: { exigencias: readonly ExigenciaDoFormulario[]; total: number } | null,
  tempos: readonly TempoDoFormulario[],
  marcados: ReadonlySet<string>,
): boolean {
  if (!pedido) return true
  const n = contagem(pedido.exigencias, tempos, marcados)
  const livres = tempos.filter((t) => marcados.has(chaveDoTempo(t))).length
  return pedido.exigencias.every((e, i) => n[i]! >= e.aulas) && livres >= pedido.total
}

export const hora = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`

/**
 * As linhas da tabela de um turno: os horários distintos, em ordem.
 * Dias diferentes podem ter horários diferentes; a célula que não existe
 * naquele dia fica vazia, não clicável.
 */
export function linhasDoTurno(tempos: readonly TempoDoFormulario[], turnoId: string) {
  const vistas = new Map<string, { inicio: number; fim: number }>()
  for (const t of tempos) {
    if (t.turno_id !== turnoId) continue
    vistas.set(`${t.inicio}-${t.fim}`, { inicio: t.inicio, fim: t.fim })
  }
  return [...vistas.values()].sort((a, b) => a.inicio - b.inicio || a.fim - b.fim)
}
